import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import FolderOpenRoundedIcon from "@mui/icons-material/FolderOpenRounded";
import PriceChangeRoundedIcon from "@mui/icons-material/PriceChangeRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import WorkspacesRoundedIcon from "@mui/icons-material/WorkspacesRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ownerApi } from "../../../api/owner";
import { formatApiError } from "../../../api/client";
import { idOf, surfaceSx } from "./ownerUi";
import { OwnerBasePricingDialog } from "./components/OwnerProjectCommercialPanel";

/*
 * WP-STANDARD | OWNER UNIFIED PEOPLE / PROJECTS DIRECTORY
 * Status: IMPLEMENTED · STATIC_TESTED · RUNTIME_PENDING
 *
 * Public mental model:
 *   Person -> one or more projects -> optional WebProfit projects.
 * Persistence stays normalized (OwnerClient + OwnerInitiative + Project), but users never
 * need to jump between a separate Clients page and a Projects page again.
 * UI rule: one dense management table row per person; project detail expands in-place.
 */

const EMPTY_PERSON = { name: "", company: "", email: "", phone: "", preferredChannel: "whatsapp" };
const EMPTY_PROJECT = { name: "", clientId: "", domain: "business", priority: "normal", outcome: "", notes: "" };

function UnifiedCreateDialog({ open, clients, onClose, onSaved }) {
  const [mode, setMode] = React.useState("person");
  const [person, setPerson] = React.useState(EMPTY_PERSON);
  const [project, setProject] = React.useState(EMPTY_PROJECT);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!open) return;
    setMode("person");
    setPerson(EMPTY_PERSON);
    setProject(EMPTY_PROJECT);
    setError("");
  }, [open]);

  const submit = async () => {
    setError("");
    setSaving(true);
    try {
      if (mode === "person") {
        if (!person.name.trim()) throw new Error("Poné el nombre de la persona.");
        const projectName = project.name.trim() || person.company.trim() || person.name.trim();
        const response = await ownerApi.createClient({
          name: person.name.trim(),
          company: person.company.trim(),
          email: person.email.trim(),
          phone: person.phone.trim(),
          preferredChannel: person.preferredChannel || "",
          status: "active",
          relationshipType: "client",
          crmStage: "customer",
          source: "Owner",
          baseProject: {
            name: projectName,
            domain: project.domain,
            priority: project.priority,
            outcome: project.outcome.trim(),
            notes: project.notes.trim(),
          },
        });
        await onSaved?.(response?.baseInitiative || null);
      } else {
        if (!project.clientId) throw new Error("Elegí la persona a la que pertenece el proyecto.");
        if (!project.name.trim()) throw new Error("Poné un nombre al proyecto.");
        const response = await ownerApi.createInitiative({
          name: project.name.trim(),
          clientId: project.clientId,
          domain: project.domain,
          priority: project.priority,
          outcome: project.outcome.trim(),
          notes: project.notes.trim(),
        });
        await onSaved?.(response?.initiative || null);
      }
      onClose?.();
    } catch (err) {
      setError(formatApiError(err, err?.message || "No pude crear el registro."));
    } finally {
      setSaving(false);
    }
  };

  return <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
    <DialogTitle sx={{ fontWeight: 1000 }}>Nuevo</DialogTitle>
    <DialogContent>
      <Stack spacing={1.05} sx={{ pt: .35 }}>
        {error ? <Alert severity="error">{error}</Alert> : null}
        <TextField select size="small" label="Crear" value={mode} onChange={(event) => setMode(event.target.value)}>
          <MenuItem value="person">Persona + proyecto base</MenuItem>
          <MenuItem value="project">Proyecto para una persona existente</MenuItem>
        </TextField>

        {mode === "person" ? <>
          <Typography sx={{ fontSize: ".68rem", fontWeight: 1000, color: "text.secondary" }}>PERSONA</Typography>
          <TextField autoFocus label="Nombre" value={person.name} onChange={(event) => setPerson((current) => ({ ...current, name: event.target.value }))} />
          <TextField label="Empresa / marca" value={person.company} onChange={(event) => setPerson((current) => ({ ...current, company: event.target.value }))} />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <TextField fullWidth label="Email" value={person.email} onChange={(event) => setPerson((current) => ({ ...current, email: event.target.value }))} />
            <TextField fullWidth label="Teléfono" value={person.phone} onChange={(event) => setPerson((current) => ({ ...current, phone: event.target.value }))} />
          </Stack>
          <TextField select label="Canal preferido" value={person.preferredChannel} onChange={(event) => setPerson((current) => ({ ...current, preferredChannel: event.target.value }))}>
            <MenuItem value="whatsapp">WhatsApp</MenuItem><MenuItem value="phone">Teléfono</MenuItem><MenuItem value="email">Email</MenuItem><MenuItem value="instagram">Instagram</MenuItem><MenuItem value="in_person">Presencial</MenuItem><MenuItem value="other">Otro</MenuItem><MenuItem value="">Sin definir</MenuItem>
          </TextField>
          <Typography sx={{ fontSize: ".68rem", fontWeight: 1000, color: "text.secondary", mt: .25 }}>PROYECTO BASE</Typography>
          <TextField label="Nombre del proyecto" placeholder="Si lo dejás vacío usa empresa o persona" value={project.name} onChange={(event) => setProject((current) => ({ ...current, name: event.target.value }))} />
        </> : <>
          <TextField select label="Persona" value={project.clientId} onChange={(event) => setProject((current) => ({ ...current, clientId: event.target.value }))}>
            {clients.map((client) => <MenuItem key={idOf(client)} value={idOf(client)}>{client.name}{client.company ? ` · ${client.company}` : ""}</MenuItem>)}
          </TextField>
          <TextField autoFocus label="Nombre del proyecto" value={project.name} onChange={(event) => setProject((current) => ({ ...current, name: event.target.value }))} />
        </>}

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <TextField select fullWidth label="Área" value={project.domain} onChange={(event) => setProject((current) => ({ ...current, domain: event.target.value }))}>
            <MenuItem value="business">Negocios</MenuItem><MenuItem value="freelance">Freelance</MenuItem><MenuItem value="personal">Personal</MenuItem><MenuItem value="home">Hogar</MenuItem><MenuItem value="career">Carrera</MenuItem><MenuItem value="finance">Finanzas</MenuItem><MenuItem value="other">Otro</MenuItem>
          </TextField>
          <TextField select fullWidth label="Prioridad" value={project.priority} onChange={(event) => setProject((current) => ({ ...current, priority: event.target.value }))}>
            <MenuItem value="low">Baja</MenuItem><MenuItem value="normal">Normal</MenuItem><MenuItem value="high">Alta</MenuItem>
          </TextField>
        </Stack>
        <TextField label="Resultado / objetivo" value={project.outcome} onChange={(event) => setProject((current) => ({ ...current, outcome: event.target.value }))} />
        <TextField label="Notas del proyecto" multiline minRows={2} value={project.notes} onChange={(event) => setProject((current) => ({ ...current, notes: event.target.value }))} />
      </Stack>
    </DialogContent>
    <DialogActions><Button onClick={onClose} disabled={saving}>Cancelar</Button><Button variant="contained" onClick={submit} disabled={saving}>{saving ? "Guardando…" : "Crear"}</Button></DialogActions>
  </Dialog>;
}

function isWebProfitProject(row) {
  return Boolean(row?.webProfitProjectId || row?.webProfitProject?.id || (Array.isArray(row?.webProfitProjectIds) && row.webProfitProjectIds.length));
}

function normalize(value) { return String(value || "").trim().toLowerCase(); }

function projectMatchesQuery(row, normalizedQuery) {
  if (!normalizedQuery) return true;
  return [row?.name, row?.webProfitProject?.name, row?.webProfitProject?.slug, row?.nextStep, row?.outcome, row?.notes]
    .some((value) => normalize(value).includes(normalizedQuery));
}

function personMatchesQuery(client, normalizedQuery) {
  if (!normalizedQuery) return true;
  return [client?.name, client?.company, client?.email, client?.phone].some((value) => normalize(value).includes(normalizedQuery));
}

function buildPersonGroups(rows = [], clients = []) {
  const groups = new Map();
  clients.forEach((client, index) => groups.set(`client:${idOf(client)}`, { key: `client:${idOf(client)}`, client, rows: [], firstIndex: index }));

  rows.forEach((row, index) => {
    const clientId = idOf(row?.client) || String(row?.clientId || "");
    const key = clientId ? `client:${clientId}` : `initiative:${idOf(row) || index}`;
    const current = groups.get(key) || { key, client: row?.client || null, rows: [], firstIndex: clients.length + index };
    if (!current.client && row?.client) current.client = row.client;
    current.rows.push(row);
    groups.set(key, current);
  });

  return [...groups.values()].sort((a, b) => {
    const nameA = normalize(a?.client?.name || a?.client?.company || a?.rows?.[0]?.name);
    const nameB = normalize(b?.client?.name || b?.client?.company || b?.rows?.[0]?.name);
    return nameA.localeCompare(nameB, "es");
  });
}

function communicationLabel(client) {
  const channel = String(client?.preferredChannel || "");
  const hasPhone = Boolean(client?.phone);
  const hasEmail = Boolean(client?.email);
  if ((channel === "whatsapp" || channel === "phone") && hasPhone) return "Listo";
  if (channel === "email" && hasEmail) return "Listo";
  if (hasPhone || hasEmail) return "Canal pendiente";
  return "Sin contacto";
}

function channelLabel(value) {
  return ({ whatsapp: "WhatsApp", phone: "Teléfono", email: "Email", instagram: "Instagram", in_person: "Presencial", other: "Otro" })[String(value || "")] || "Sin definir";
}

const PROJECT_PIPELINE_OPTIONS = [
  { value: "lead", label: "Lead potencial" },
  { value: "active", label: "Activo" },
  { value: "paused", label: "Pausado" },
  { value: "inactive", label: "Desactivado" },
];

const PROJECT_PRIORITY_OPTIONS = [
  { value: "low", label: "Baja" },
  { value: "normal", label: "Normal" },
  { value: "high", label: "Alta" },
];

// WP-OWNER-PROTOCOL | PATCH 3
// Responsibility/attention are Owner mental-scope metadata; changing them must never
// mutate project pipeline state, finance or linked WebProfit projects.
// Test status: backend UNIT_TESTED; panel STATIC_REVIEWED, runtime pending.
const PROJECT_RESPONSIBILITY_OPTIONS = [
  { value: "owner", label: "Mía" },
  { value: "delegated", label: "Delegada" },
  { value: "shared", label: "Compartida" },
  { value: "observer", label: "Sólo observar" },
  { value: "none", label: "Fuera de mi operación" },
];
const PROJECT_ATTENTION_OPTIONS = [
  { value: "active", label: "Activa" },
  { value: "watch", label: "Vigilar" },
  { value: "quiet", label: "Silenciosa" },
];
function ownerAttentionOf(row) { return row?.ownerAttention || { responsibility: "owner", baseAttention: "active", attention: "active", effectiveAttention: "active" }; }
function matchesOwnerScope(row, filter) {
  if (filter === "all") return true;
  const meta = ownerAttentionOf(row);
  if (filter === "mine") return ["owner", "shared"].includes(meta.responsibility);
  if (filter === "delegated") return meta.responsibility === "delegated";
  if (filter === "observer") return meta.responsibility === "observer" || meta.baseAttention === "watch";
  if (filter === "quiet") return meta.responsibility === "none" || meta.baseAttention === "quiet";
  return true;
}

function matchesPipelineFilter(row, filter) {
  if (filter === "all") return true;
  const current = String(row?.status || "active");
  if (filter === "inactive") return ["inactive", "done", "archived"].includes(current);
  return current === filter;
}

function formatShortDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("es-AR", { day: "2-digit", month: "short" }).format(date);
}

function personProjectStats(rows = []) {
  const webProfitRows = rows.filter(isWebProfitProject);
  const ownRows = rows.filter((row) => !isWebProfitProject(row));
  const lead = rows.filter((row) => String(row?.status || "active") === "lead").length;
  const active = rows.filter((row) => String(row?.status || "active") === "active").length;
  const paused = rows.filter((row) => String(row?.status || "active") === "paused").length;
  const inactive = rows.filter((row) => ["inactive", "done", "archived"].includes(String(row?.status || "active"))).length;
  const overdue = rows.reduce((sum, row) => sum + Number(row?.finance?.overdueIncomeCount || 0), 0);
  const recurring = rows.reduce((sum, row) => sum + Number(row?.finance?.recurringCount || 0), 0);
  return { webProfitRows, ownRows, lead, active, paused, inactive, overdue, recurring };
}

function ProjectCountCell({ rows, kind }) {
  if (!rows.length) return <Typography sx={{ color: "text.disabled", fontSize: ".78rem" }}>—</Typography>;
  const first = rows[0];
  return <Box sx={{ minWidth: 0 }}>
    <Stack direction="row" spacing={.45} alignItems="center">
      {kind === "webprofit" ? <LanguageRoundedIcon sx={{ fontSize: 15, color: "primary.main" }} /> : <WorkspacesRoundedIcon sx={{ fontSize: 15, color: "text.secondary" }} />}
      <Typography sx={{ fontWeight: 950, fontSize: ".78rem" }}>{rows.length}</Typography>
    </Stack>
    <Typography noWrap sx={{ maxWidth: 170, color: "text.secondary", fontSize: ".67rem", mt: .12 }}>{first?.name || "Proyecto"}{rows.length > 1 ? ` +${rows.length - 1}` : ""}</Typography>
  </Box>;
}

function PersonProjectsExpanded({ rows, onOpen, onEdit, onPatch, updatingIds }) {
  return <Box sx={{ px: { xs: .35, md: 1.1 }, py: .8, bgcolor: "action.hover" }}>
    <Table size="small" aria-label="Proyectos de la persona" sx={{ minWidth: 1220, "& td": { borderColor: "divider" } }}>
      <TableHead>
        <TableRow sx={{ "& th": { py: .55, fontSize: ".62rem", fontWeight: 1000, textTransform: "uppercase", letterSpacing: ".04em", color: "text.secondary" } }}>
          <TableCell>Tipo</TableCell><TableCell>Proyecto</TableCell><TableCell>Estado</TableCell><TableCell>Responsabilidad</TableCell><TableCell>Responsable</TableCell><TableCell>Atención</TableCell><TableCell>Prioridad</TableCell><TableCell>Objetivo / siguiente</TableCell><TableCell align="center">Recurrentes</TableCell><TableCell align="right">Acciones</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => {
          const wp = isWebProfitProject(row);
          const rowId = idOf(row);
          const updating = updatingIds?.has(rowId);
          return <TableRow key={rowId} hover sx={{ "& td": { py: .48 } }}>
            <TableCell sx={{ width: 120 }}>
              <Chip size="small" variant="outlined" icon={wp ? <LanguageRoundedIcon /> : <WorkspacesRoundedIcon />} label={wp ? "WebProfit" : "Propio"} sx={{ height: 22, fontSize: ".64rem", fontWeight: 900 }} />
            </TableCell>
            <TableCell sx={{ minWidth: 190 }}>
              <Stack direction="row" spacing={.45} alignItems="center">
                <Typography sx={{ fontWeight: 950, fontSize: ".77rem" }}>{row?.name || "Proyecto"}</Typography>
                {row?.clientBase ? <Chip size="small" label="Base" sx={{ height: 18, fontSize: ".56rem", fontWeight: 900 }} /> : null}
              </Stack>
              {wp && row?.webProfitProject?.name && row.webProfitProject.name !== row?.name ? <Typography sx={{ color: "text.secondary", fontSize: ".64rem" }}>WP · {row.webProfitProject.name}</Typography> : null}
            </TableCell>
            <TableCell sx={{ width: 158 }}>
              <TextField select size="small" value={["done", "archived"].includes(String(row?.status)) ? "inactive" : String(row?.status || "active")} disabled={updating} onChange={(event) => onPatch?.(row, { status: event.target.value })} sx={{ minWidth: 142, "& .MuiInputBase-root": { height: 30, fontSize: ".7rem", fontWeight: 900 } }}>
                {PROJECT_PIPELINE_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
              </TextField>
              {wp && row?.pipelineManaged !== true && String(row?.status) === "lead" ? <Typography sx={{ mt: .18, color: "text.secondary", fontSize: ".56rem" }}>Detectado desde WP</Typography> : null}
            </TableCell>
            <TableCell sx={{ width: 158 }}>
              <TextField select size="small" value={ownerAttentionOf(row).responsibility || "owner"} disabled={updating} onChange={(event) => onPatch?.(row, { ownerAttention: { responsibility: event.target.value, attention: ownerAttentionOf(row).baseAttention || ownerAttentionOf(row).attention || "active", responsibleLabel: ownerAttentionOf(row).responsibleParty?.label || "" } })} sx={{ minWidth: 145, "& .MuiInputBase-root": { height: 30, fontSize: ".68rem", fontWeight: 900 } }}>
                {PROJECT_RESPONSIBILITY_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
              </TextField>
            </TableCell>
            <TableCell sx={{ width: 155 }}>
              {["delegated", "shared"].includes(ownerAttentionOf(row).responsibility) ? <TextField size="small" defaultValue={ownerAttentionOf(row).responsibleParty?.label || ""} disabled={updating} placeholder="Persona" onBlur={(event) => { const value = event.target.value.trim(); if (value !== String(ownerAttentionOf(row).responsibleParty?.label || "")) onPatch?.(row, { ownerAttention: { responsibility: ownerAttentionOf(row).responsibility, attention: ownerAttentionOf(row).baseAttention || "watch", responsibleLabel: value } }); }} sx={{ minWidth: 140, "& .MuiInputBase-root": { height: 30, fontSize: ".68rem" } }} /> : <Typography sx={{ fontSize: ".68rem", color: "text.disabled" }}>—</Typography>}
            </TableCell>
            <TableCell sx={{ width: 125 }}>
              <TextField select size="small" value={ownerAttentionOf(row).baseAttention || ownerAttentionOf(row).attention || "active"} disabled={updating} onChange={(event) => onPatch?.(row, { ownerAttention: { responsibility: ownerAttentionOf(row).responsibility || "owner", attention: event.target.value, responsibleLabel: ownerAttentionOf(row).responsibleParty?.label || "" } })} sx={{ minWidth: 112, "& .MuiInputBase-root": { height: 30, fontSize: ".68rem", fontWeight: 900 } }}>
                {PROJECT_ATTENTION_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
              </TextField>
              {ownerAttentionOf(row).effectiveAttention !== ownerAttentionOf(row).baseAttention ? <Typography sx={{ mt: .15, fontSize: ".54rem", color: "warning.main", fontWeight: 900 }}>Excepción → {ownerAttentionOf(row).effectiveAttention}</Typography> : null}
            </TableCell>
            <TableCell sx={{ width: 122 }}>
              <TextField select size="small" value={String(row?.priority || "normal")} disabled={updating} onChange={(event) => onPatch?.(row, { priority: event.target.value })} sx={{ minWidth: 108, "& .MuiInputBase-root": { height: 30, fontSize: ".7rem", fontWeight: 900 } }}>
                {PROJECT_PRIORITY_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
              </TextField>
            </TableCell>
            <TableCell sx={{ maxWidth: 300 }}><Typography noWrap sx={{ fontSize: ".72rem", color: row?.nextStep || row?.outcome ? "text.primary" : "text.disabled" }}>{row?.nextStep || row?.outcome || "Sin próximo paso"}</Typography></TableCell>
            <TableCell align="center"><Typography sx={{ fontSize: ".74rem", fontWeight: 850 }}>{Number(row?.finance?.recurringCount || 0)}</Typography></TableCell>
            <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
              <Tooltip title="Editar ficha"><IconButton size="small" disabled={updating} onClick={(event) => { event.stopPropagation(); onEdit?.(row); }}><EditRoundedIcon sx={{ fontSize: 17 }} /></IconButton></Tooltip>
              <Tooltip title="Abrir Proyecto 360"><IconButton size="small" color="primary" disabled={updating} onClick={(event) => { event.stopPropagation(); onOpen?.(row); }}><OpenInNewRoundedIcon sx={{ fontSize: 17 }} /></IconButton></Tooltip>
            </TableCell>
          </TableRow>;
        })}
      </TableBody>
    </Table>
  </Box>;
}

function PersonProjectTableRow({ group, rows, expanded, onToggle, onOpen, onEdit, onPatch, updatingIds }) {
  const clientName = group?.client?.name || group?.client?.company || "Proyecto sin persona";
  const company = group?.client?.company && group.client.company !== clientName ? group.client.company : "";
  const primary = rows.find((row) => row.clientBase) || rows[0] || null;
  const stats = personProjectStats(rows);
  const crm = group?.client?.crm || {};
  const followUp = crm?.nextAction || "";
  const followUpAt = formatShortDate(crm?.nextActionAt);
  const contactState = communicationLabel(group?.client);

  return <>
    <TableRow hover selected={expanded} sx={{ cursor: "pointer", "& > td": { py: .72, borderColor: "divider" } }} onClick={onToggle}>
      <TableCell padding="checkbox" sx={{ width: 42 }}>
        <IconButton size="small" aria-label={expanded ? "Contraer proyectos" : "Expandir proyectos"} onClick={(event) => { event.stopPropagation(); onToggle?.(); }}>
          {expanded ? <KeyboardArrowDownRoundedIcon /> : <KeyboardArrowRightRoundedIcon />}
        </IconButton>
      </TableCell>
      <TableCell sx={{ minWidth: 210 }}>
        <Stack direction="row" spacing={.75} alignItems="center">
          <Box sx={{ width: 30, height: 30, borderRadius: "50%", display: "grid", placeItems: "center", bgcolor: "action.selected", flex: "0 0 auto" }}><PersonRoundedIcon sx={{ fontSize: 17, color: "text.secondary" }} /></Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontWeight: 1000, fontSize: ".8rem" }}>{clientName}</Typography>
            <Typography noWrap sx={{ maxWidth: 230, color: "text.secondary", fontSize: ".65rem" }}>{company || group?.client?.email || `${rows.length} proyecto${rows.length === 1 ? "" : "s"}`}</Typography>
          </Box>
        </Stack>
      </TableCell>
      <TableCell sx={{ minWidth: 180, display: { xs: "none", lg: "table-cell" } }}>
        <Typography noWrap sx={{ fontSize: ".72rem", fontWeight: 850 }}>{group?.client?.phone || group?.client?.email || "Sin contacto"}</Typography>
        <Stack direction="row" spacing={.35} alignItems="center" sx={{ mt: .18 }}>
          <Typography sx={{ color: "text.secondary", fontSize: ".62rem" }}>{channelLabel(group?.client?.preferredChannel)}</Typography>
          <Typography sx={{ color: contactState === "Listo" ? "success.main" : "warning.main", fontSize: ".6rem", fontWeight: 900 }}>· {contactState}</Typography>
        </Stack>
      </TableCell>
      <TableCell sx={{ width: 175 }}><ProjectCountCell rows={stats.ownRows} kind="own" /></TableCell>
      <TableCell sx={{ width: 175 }}><ProjectCountCell rows={stats.webProfitRows} kind="webprofit" /></TableCell>
      <TableCell sx={{ minWidth: 190, display: { xs: "none", xl: "table-cell" } }}>
        <Typography noWrap sx={{ maxWidth: 220, fontSize: ".7rem", fontWeight: followUp ? 800 : 500, color: followUp ? "text.primary" : "text.disabled" }}>{followUp || "Sin seguimiento"}</Typography>
        {followUpAt ? <Typography sx={{ color: "text.secondary", fontSize: ".61rem", mt: .12 }}>{followUpAt}</Typography> : null}
      </TableCell>
      <TableCell sx={{ minWidth: 135, display: { xs: "none", md: "table-cell" } }}>
        <Stack direction="row" spacing={.35} flexWrap="wrap" useFlexGap>
          {stats.lead ? <Chip size="small" color="info" variant="outlined" label={`${stats.lead} lead${stats.lead === 1 ? "" : "s"}`} sx={{ height: 20, fontSize: ".58rem", fontWeight: 900 }} /> : null}
          {stats.active ? <Chip size="small" color="success" variant="outlined" label={`${stats.active} activo${stats.active === 1 ? "" : "s"}`} sx={{ height: 20, fontSize: ".58rem", fontWeight: 900 }} /> : null}
          {stats.paused ? <Chip size="small" color="warning" variant="outlined" label={`${stats.paused} pausado${stats.paused === 1 ? "" : "s"}`} sx={{ height: 20, fontSize: ".58rem", fontWeight: 900 }} /> : null}
          {stats.inactive ? <Chip size="small" variant="outlined" label={`${stats.inactive} desact.`} sx={{ height: 20, fontSize: ".58rem", fontWeight: 900 }} /> : null}
          {stats.overdue ? <Chip size="small" color="error" variant="outlined" label={`${stats.overdue} venc.`} sx={{ height: 20, fontSize: ".58rem", fontWeight: 900 }} /> : null}
        </Stack>
      </TableCell>
      <TableCell align="right" sx={{ width: 100, whiteSpace: "nowrap" }}>
        {primary ? <>
          <Tooltip title="Editar ficha"><IconButton size="small" onClick={(event) => { event.stopPropagation(); onEdit?.(primary); }}><EditRoundedIcon sx={{ fontSize: 17 }} /></IconButton></Tooltip>
          <Tooltip title="Abrir proyecto principal"><IconButton size="small" color="primary" onClick={(event) => { event.stopPropagation(); onOpen?.(primary); }}><OpenInNewRoundedIcon sx={{ fontSize: 17 }} /></IconButton></Tooltip>
        </> : null}
      </TableCell>
    </TableRow>
    <TableRow>
      <TableCell colSpan={8} sx={{ py: 0, px: 0, borderBottom: expanded ? "1px solid" : 0, borderColor: "divider" }}>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <PersonProjectsExpanded rows={rows} onOpen={onOpen} onEdit={onEdit} onPatch={onPatch} updatingIds={updatingIds} />
        </Collapse>
      </TableCell>
    </TableRow>
  </>;
}

export default function OwnerProjectsPage() {
  const navigate = useNavigate();
  // WP-OWNER-PROTOCOL | Read-only CRM navigation recovery. A search query only
  // filters the EXISTING Owner project directory; it cannot mutate Billing.
  // Test status: UNIT_TESTED (static); browser E2E NOT_TESTED.
  const [searchParams] = useSearchParams();
  const [rows, setRows] = React.useState([]);
  const [clients, setClients] = React.useState([]);
  const [query, setQuery] = React.useState(() => searchParams.get("buscar") || "");
  React.useEffect(() => { setQuery(searchParams.get("buscar") || ""); }, [searchParams]);
  const [status, setStatus] = React.useState("all");
  const [projectType, setProjectType] = React.useState("all");
  const [ownerScope, setOwnerScope] = React.useState("all");
  const [createOpen, setCreateOpen] = React.useState(false);
  const [basePricingOpen, setBasePricingOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [expandedPeople, setExpandedPeople] = React.useState(() => new Set());
  const [updatingIds, setUpdatingIds] = React.useState(() => new Set());

  const load = React.useCallback(async () => {
    setLoading(true); setError("");
    try {
      // Lazy migration + invariant repair for legacy people that existed only in Clientes.
      // It is idempotent; after the first pass it becomes a no-op.
      await ownerApi.ensureClientProjects().catch(() => null);
      const [projectResponse, clientResponse] = await Promise.all([
        ownerApi.listInitiatives({ status: "", limit: 500 }),
        ownerApi.listClients({ limit: 500 }),
      ]);
      setRows(projectResponse?.initiatives || []);
      setClients(clientResponse?.clients || []);
    } catch (err) { setError(formatApiError(err, "No pude cargar personas y proyectos.")); }
    finally { setLoading(false); }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const normalizedQuery = normalize(query);
  const rawGroups = React.useMemo(() => buildPersonGroups(rows, clients), [rows, clients]);
  const groups = React.useMemo(() => rawGroups.map((group) => {
    const personMatched = personMatchesQuery(group.client, normalizedQuery);
    const visibleRows = group.rows.filter((row) => {
      if (!matchesPipelineFilter(row, status)) return false;
      if (!matchesOwnerScope(row, ownerScope)) return false;
      if (projectType === "webprofit" && !isWebProfitProject(row)) return false;
      if (projectType === "own" && isWebProfitProject(row)) return false;
      return personMatched || projectMatchesQuery(row, normalizedQuery);
    });
    return { ...group, visibleRows };
  }).filter((group) => group.visibleRows.length), [rawGroups, normalizedQuery, status, projectType, ownerScope]);

  const webProfitCount = React.useMemo(() => rows.filter(isWebProfitProject).length, [rows]);
  const ownCount = rows.length - webProfitCount;
  const openProject = React.useCallback((row) => navigate(`/dashboard/owner/proyectos/${encodeURIComponent(idOf(row))}`), [navigate]);
  const editProject = React.useCallback((row) => navigate(`/dashboard/owner/proyectos/${encodeURIComponent(idOf(row))}?editar=1`), [navigate]);
  const togglePerson = React.useCallback((key) => setExpandedPeople((current) => {
    const next = new Set(current);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  }), []);

  const patchProject = React.useCallback(async (row, payload) => {
    const rowId = idOf(row);
    if (!rowId) return;
    setUpdatingIds((current) => new Set(current).add(rowId));
    setError("");
    try {
      const response = payload?.ownerAttention
        ? await ownerApi.updateInitiativeOwnerAttention(rowId, payload.ownerAttention)
        : await ownerApi.updateInitiative(rowId, payload);
      const updated = response?.initiative || { ...row, ...payload };
      setRows((current) => current.map((item) => idOf(item) === rowId ? { ...item, ...updated } : item));
    } catch (err) {
      setError(formatApiError(err, "No pude actualizar la clasificación del proyecto."));
    } finally {
      setUpdatingIds((current) => { const next = new Set(current); next.delete(rowId); return next; });
    }
  }, []);

  return <Box sx={{ width: "100%" }}>
    <Stack direction={{ xs: "column", md: "row" }} spacing={.55} sx={{ mb: .7 }}>
      <TextField fullWidth size="small" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar persona, empresa, contacto o proyecto" InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }} />
      <Stack direction="row" spacing={.55} sx={{ minWidth: 0 }}>
        <TextField select size="small" value={projectType} onChange={(event) => setProjectType(event.target.value)} sx={{ minWidth: { xs: 0, sm: 165 }, flex: { xs: 1, sm: "0 0 auto" } }}>
          <MenuItem value="all">Todos · {rows.length}</MenuItem><MenuItem value="webprofit">WebProfit · {webProfitCount}</MenuItem><MenuItem value="own">Propios · {ownCount}</MenuItem>
        </TextField>
        <TextField select size="small" value={ownerScope} onChange={(event) => setOwnerScope(event.target.value)} sx={{ minWidth: { xs: 0, sm: 155 }, flex: { xs: 1, sm: "0 0 auto" } }}>
          <MenuItem value="all">Toda atención</MenuItem><MenuItem value="mine">Mis proyectos</MenuItem><MenuItem value="delegated">Delegados</MenuItem><MenuItem value="observer">En observación</MenuItem><MenuItem value="quiet">Fuera de atención</MenuItem>
        </TextField>
        <TextField select size="small" value={status} onChange={(event) => setStatus(event.target.value)} sx={{ minWidth: { xs: 0, sm: 135 }, flex: { xs: 1, sm: "0 0 auto" } }}>
          <MenuItem value="lead">Leads potenciales</MenuItem><MenuItem value="active">Activos</MenuItem><MenuItem value="paused">Pausados</MenuItem><MenuItem value="inactive">Desactivados</MenuItem><MenuItem value="all">Todos</MenuItem>
        </TextField>
      </Stack>
      <Stack direction="row" spacing={.55}>
        <Button fullWidth variant="outlined" color="inherit" size="small" startIcon={<PriceChangeRoundedIcon />} onClick={() => setBasePricingOpen(true)} sx={{ minHeight: 40, textTransform: "none", fontWeight: 950, whiteSpace: "nowrap" }}>Precios base</Button>
        <Button fullWidth variant="contained" size="small" startIcon={<AddRoundedIcon />} onClick={() => setCreateOpen(true)} sx={{ minHeight: 40, textTransform: "none", fontWeight: 950, whiteSpace: "nowrap" }}>Nuevo</Button>
      </Stack>
    </Stack>

    {error ? <Alert severity="error" sx={{ mb: .7 }}>{error}</Alert> : null}
    {!loading && !groups.length ? <Box sx={{ ...surfaceSx, p: 4, textAlign: "center" }}><FolderOpenRoundedIcon sx={{ fontSize: 34, color: "text.disabled" }} /><Typography sx={{ mt: .5, fontWeight: 950 }}>No hay resultados.</Typography></Box> : null}

    {!loading && groups.length ? <Box sx={{ ...surfaceSx, overflow: "hidden" }}>
      <Box sx={{ px: 1.15, py: .7, borderBottom: "1px solid", borderColor: "divider", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Typography sx={{ fontSize: ".68rem", fontWeight: 900, color: "text.secondary" }}>{groups.length} persona{groups.length === 1 ? "" : "s"} · {groups.reduce((sum, group) => sum + group.visibleRows.length, 0)} proyectos</Typography>
        <Typography sx={{ display: { xs: "none", sm: "block" }, fontSize: ".62rem", color: "text.disabled" }}>Click en una fila para ver todos sus proyectos</Typography>
      </Box>
      <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
        <Table stickyHeader size="small" aria-label="Personas y proyectos" sx={{ minWidth: { xs: 690, md: 980 }, "& th": { bgcolor: "background.paper" } }}>
          <TableHead>
            <TableRow sx={{ "& th": { py: .72, borderColor: "divider", fontSize: ".62rem", fontWeight: 1000, textTransform: "uppercase", letterSpacing: ".04em", color: "text.secondary", whiteSpace: "nowrap" } }}>
              <TableCell padding="checkbox" />
              <TableCell>Persona / empresa</TableCell>
              <TableCell sx={{ display: { xs: "none", lg: "table-cell" } }}>Contacto</TableCell>
              <TableCell>Propios</TableCell>
              <TableCell>WebProfit</TableCell>
              <TableCell sx={{ display: { xs: "none", xl: "table-cell" } }}>Seguimiento</TableCell>
              <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>Estado</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map((group) => <PersonProjectTableRow key={group.key} group={group} rows={group.visibleRows} expanded={expandedPeople.has(group.key)} onToggle={() => togglePerson(group.key)} onOpen={openProject} onEdit={editProject} onPatch={patchProject} updatingIds={updatingIds} />)}
          </TableBody>
        </Table>
      </TableContainer>
    </Box> : null}

    <UnifiedCreateDialog open={createOpen} clients={clients} onClose={() => setCreateOpen(false)} onSaved={async (created) => {
      await load();
      if (created) navigate(`/dashboard/owner/proyectos/${encodeURIComponent(idOf(created))}`);
    }} />
    <OwnerBasePricingDialog open={basePricingOpen} onClose={() => setBasePricingOpen(false)} />
  </Box>;
}
